import {
  InventoryItem,
  UsedInventoriesForDeliveryShop,
} from '../../../models/config-strip-state.models';
import { isTrueDemandInventory } from '../../../shared/true-demand-inventory';

// tslint:disable-next-line:cognitive-complexity
export function calculateUsedInventories({
  difference,
  previousQuantity,
  availableInventories,
  usedInventoriesForDeliveryShop,
  deliveryShopId,
  deliveryPeriod,
}: {
  difference: number;
  previousQuantity: number;
  availableInventories: InventoryItem[];
  usedInventoriesForDeliveryShop: UsedInventoriesForDeliveryShop;
  deliveryShopId: string;
  deliveryPeriod: string;
}) {
  const productInventories = availableInventories.map(({ itemId }) => itemId);
  const selectedUsedInventory = usedInventoriesForDeliveryShop[deliveryShopId];

  const usedInventoriesForPeriodCopy1 = {
    ...(selectedUsedInventory && selectedUsedInventory[deliveryPeriod]
      ? selectedUsedInventory[deliveryPeriod]
      : {}),
  };

  let leftToClear = previousQuantity;
  // clear previous inventories
  const usedInventoriesForPeriodCopy: { [x: string]: number } = Object.entries(
    usedInventoriesForPeriodCopy1,
  ).reduceRight((acc, [key, usedQuantities]) => {
    // no need to clear inventory if it's not related to product
    if (!productInventories.includes(key))
      return { ...acc, [key]: usedQuantities };

    const val = usedQuantities - leftToClear;
    leftToClear = Math.abs(val);

    return {
      ...acc,
      [key]: Math.max(val, 0),
    };
  }, {});

  const inventoryUpdate = difference;
  let appliedQuantity = 0;
  let oldQuantity = 0;

  for (const inventory of availableInventories) {
    const restQuantitiesToApply = inventoryUpdate - appliedQuantity;

    const usedQuantity = usedInventoriesForPeriodCopy[inventory.itemId] ?? 0;
    oldQuantity += usedQuantity;
    // if consumed previously inventory is bigger then there is no need to recalculate. just
    // release inventories
    if (usedQuantity >= restQuantitiesToApply) {
      // if we decease quantity then first inventories might already got all needed
      // quantities to be applied and other needs just to be nullable
      usedInventoriesForPeriodCopy[inventory.itemId] = Math.max(
        restQuantitiesToApply,
        0,
      );
      appliedQuantity += restQuantitiesToApply;
      // we need to go over all the inventories and update there quantities in case
      // we consumed from different inventories
      continue;
    }

    const quantityToAffect = (inventory.quantity ?? 0) - usedQuantity;

    // if there is inventories left to consume
    if (quantityToAffect !== 0) {
      // Because it is possible to take or return units from an inventory, the comparison is made with the absolute values
      // !Note! Execute if we can get all needed inventories for one go
      if (Math.abs(restQuantitiesToApply) <= Math.abs(quantityToAffect)) {
        usedInventoriesForPeriodCopy[inventory.itemId] = restQuantitiesToApply;

        appliedQuantity += restQuantitiesToApply;
        break;
      } else {
        usedInventoriesForPeriodCopy[inventory.itemId] =
          usedQuantity + quantityToAffect;
        appliedQuantity = appliedQuantity + quantityToAffect + usedQuantity;
      }
    }
  }

  if (appliedQuantity === 0 && inventoryUpdate !== 0 && oldQuantity !== 0) {
    // if we don't have anything to apply, just leave the old value
    appliedQuantity = oldQuantity;
  }

  const appliedTrueDemandQuantity = Object.entries(
    usedInventoriesForPeriodCopy,
  ).reduce((sum, [itemId, quantity]) => {
    if (
      isTrueDemandInventory(itemId) &&
      availableInventories.find((i) => i.itemId === itemId)
    ) {
      sum += quantity;
    }
    return sum;
  }, 0);

  return {
    appliedQuantity,
    usedInventories: usedInventoriesForPeriodCopy,
    appliedTrueDemandQuantity,
  };
}
